import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useLocation } from "wouter";
import { useDataLoader } from "@/hooks/useDataLoader.js";
import { registerDataLoaderPause } from "@/api/api-utils.js";

/**
 * @typedef {Object} DataLoaderContextType
 * @property {() => void} triggerLoad - Function to trigger data loading
 * @property {number} triggerCount - Counter that increments on each trigger
 * @property {() => void} pauseLoader - Function to pause the data loader
 * @property {boolean} isPaused - Whether the loader is currently paused
 */

const DataLoaderContext = createContext(
  /** @type {DataLoaderContextType|null} */ (null)
);

/**
 * Hook to use the data loader context
 * @returns {DataLoaderContextType} Data loader context value
 * @throws {Error} If used outside of DataLoaderProvider
 */
export const useDataLoaderContext = () => {
  const context = useContext(DataLoaderContext);
  if (!context) {
    throw new Error(
      "useDataLoaderContext must be used within a DataLoaderProvider"
    );
  }
  return context;
};

/**
 * Data Loader Provider component that manages global data loading coordination
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {number} props.intervalMs - Interval in milliseconds (default: 15 minutes)
 * @returns {JSX.Element} Data loader provider component
 */
export function DataLoaderProvider({ children, intervalMs = 900000 }) {
  const [location] = useLocation();
  const [triggerCount, setTriggerCount] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const pathname = String(location).split("?")[0].replace(/\/$/, "") || "/";
  const isRootCauseAnalysisRoute = pathname.endsWith("/root-cause-analysis");

  const effectiveIntervalMs = useMemo(() => {
    if (isPaused) return null;
    if (isRootCauseAnalysisRoute) return null;
    return intervalMs;
  }, [intervalMs, isPaused, isRootCauseAnalysisRoute]);

  /**
   * Trigger data loading for all subscribed contexts
   */
  const triggerLoad = useCallback(() => {
    if (!isPaused) {
      setTriggerCount((prev) => prev + 1);
      // The actual loading will be handled by individual contexts
      // This just coordinates the timing
    }
  }, [isPaused]);

  /**
   * Pause the data loader to prevent further automatic updates
   */
  const pauseLoader = useCallback(() => {
    setIsPaused(true);
  }, []);

  // Register the pause function with api-utils on mount
  useEffect(() => {
    registerDataLoaderPause(pauseLoader);
  }, [pauseLoader]);

  // Use the data loader hook to manage intervals
  // Pass null as intervalMs when paused or on Root Cause Analysis (historical review; avoid mid-analysis refresh)
  useDataLoader({
    intervalMs: effectiveIntervalMs,
    onLoad: triggerLoad,
  });

  /** @type {DataLoaderContextType} */
  const value = {
    triggerLoad,
    triggerCount,
    pauseLoader,
    isPaused,
  };

  return (
    <DataLoaderContext.Provider value={value}>
      {children}
    </DataLoaderContext.Provider>
  );
}
